import React, { useState } from "react";
import { Box } from "@mui/material";
import {
  CheckCircleOutline,
  ExpandMore,
  ExpandLess,
  AttachFileOutlined,
} from "@mui/icons-material";
import { dt } from "../../utils/dt";
import { ServiceReport } from "../../models/ServiceReportsModel";
import { ImagePreviewStrip } from "../../../../shared/components/gallery";
import type { GalleryImage } from "../../../../shared/components/gallery";
import ReportFile from "../../../../shared/ReportFile";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { SectionLabel } from "./SrProblemCard";

// ===================================================================
//  CONSTANTS
// ===================================================================
const CLAMP_LINES = 5;
const CLAMP_CHAR_THRESHOLD = 300;

// ===================================================================
//  COMPONENT
// ===================================================================
interface SrSolutionCardProps {
  serviceReport: ServiceReport;
  solvingImageFiles: GalleryImage[];
  solvingNonImageFiles: Array<any>;
  isPhone: boolean;
}

const SrSolutionCard = ({
  serviceReport,
  solvingImageFiles,
  solvingNonImageFiles,
  isPhone,
}: SrSolutionCardProps) => {
  const { translate } = useTranslation("myMachinery");
  const [solutionExpanded, setSolutionExpanded] = useState(false);

  const solutionNeedsClamping =
    (serviceReport.solvingDescription?.length || 0) > CLAMP_CHAR_THRESHOLD;

  const hasAttachments =
    solvingImageFiles.length > 0 || solvingNonImageFiles.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: dt.serviceReports.detail.solutionCardBg,
        border: `1px solid ${dt.serviceReports.detail.solutionCardBorder}`,
        borderLeft: `3px solid ${dt.serviceReports.detail.solutionAccent}`,
        borderRadius: `0 ${dt.machinery.card.radius} ${dt.machinery.card.radius} 0`,
        padding: isPhone ? "16px" : "24px",
        fontFamily: dt.font,
        overflow: "hidden",
      }}
    >
      {/* Section header */}
      <SectionLabel
        label={translate("srDetail.solution")}
        accentColor={dt.serviceReports.detail.solutionAccent}
        icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
      />

      {/* Solution description */}
      {serviceReport.solvingDescription ? (
        <Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              color: dt.textSecondary,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              ...(!solutionExpanded && solutionNeedsClamping
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: CLAMP_LINES,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {serviceReport.solvingDescription}
          </Box>
          {solutionNeedsClamping && (
            <Box
              onClick={() => setSolutionExpanded(!solutionExpanded)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.serviceReports.detail.solutionAccent,
                cursor: "pointer",
                mt: 0.75,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {solutionExpanded
                ? translate("srDetail.showLess")
                : translate("srDetail.showMore")}
              {solutionExpanded ? (
                <ExpandLess sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMore sx={{ fontSize: 16 }} />
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            color: dt.textMuted,
            fontStyle: "italic",
          }}
        >
          {translate("srDetail.noSolutionDescription")}
        </Box>
      )}

      {/* Solving attachments */}
      {hasAttachments && (
        <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${dt.serviceReports.detail.solutionCardBorder}` }}>
          <SectionLabel
            label={translate("srDetail.solutionAttachments")}
            accentColor={dt.serviceReports.detail.solutionAccent}
            icon={<AttachFileOutlined sx={{ fontSize: 14 }} />}
          />
          {solvingImageFiles.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <ImagePreviewStrip
                images={solvingImageFiles}
                maxVisible={4}
                thumbnailSize={isPhone ? 48 : 64}
              />
            </Box>
          )}
          {solvingNonImageFiles.length > 0 && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
            >
              {solvingNonImageFiles.map((file: any, i: number) => (
                <ReportFile
                  key={i}
                  file={file}
                  canDelete={false}
                  canDownload={true}
                  fileIndex={i}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SrSolutionCard;
